import { api } from '@/lib/axios';
import { type PdfMode, renderPdf } from '@/lib/pdf';
import type { Page } from '@/services/chantiers';

export type ArticleCategorie = 'MATERIAUX' | 'CONSOMMABLE' | 'EPI' | 'PIECE' | 'CARBURANT' | 'OUTILLAGE';
export type DepotType = 'CENTRAL' | 'CHANTIER';
export type MouvementType = 'ENTREE' | 'SORTIE' | 'TRANSFERT' | 'REGUL';
export type BonSortieStatut = 'BROUILLON' | 'VALIDE' | 'ANNULE';

export const ARTICLE_CATEGORIES: ArticleCategorie[] = ['MATERIAUX', 'CONSOMMABLE', 'EPI', 'PIECE', 'CARBURANT', 'OUTILLAGE'];
export const CATEGORIE_LABEL: Record<ArticleCategorie, string> = {
  MATERIAUX: 'Matériaux', CONSOMMABLE: 'Consommable', EPI: 'EPI', PIECE: 'Pièce', CARBURANT: 'Carburant', OUTILLAGE: 'Outillage',
};
export const UNITES_STOCK = ['u', 'm', 'm2', 'm3', 'kg', 'tonne', 'sac', 'ml', 'l'];

export interface ArticleDTO {
  id: string;
  reference: string | null;
  designation: string;
  categorie: ArticleCategorie;
  unite: string | null;
  stockable: boolean;
  seuil_min: number;
  actif: boolean;
  cmp_courant: number;
  stock_total: number;
  valeur_stock: number;
  sous_seuil: boolean;
}

export interface ArticleInput {
  reference?: string | null;
  designation: string;
  categorie: ArticleCategorie;
  unite?: string | null;
  stockable: boolean;
  seuil_min: number;
  actif?: boolean;
}

export interface DepotDTO {
  id: string;
  code: string;
  libelle: string;
  type: DepotType;
  chantier_id: string | null;
  adresse: string | null;
  actif: boolean;
}

export interface MouvementDTO {
  id: string;
  reference: string | null;
  type: MouvementType;
  article_id: string;
  depot_id: string;
  quantite: number;
  prix_unitaire: number;
  valeur: number;
  document_type: string | null;
  chantier_id: string | null;
  motif: string | null;
  date_mouvement: string | null;
}

export interface StockDepotDTO { depot_id: string; depot_libelle: string; quantite: number }

export interface LigneBonSortieDTO { id: string; article_id: string; quantite: number; cmp_applique: number; valeur: number }

export interface BonSortieDTO {
  id: string;
  reference: string | null;
  depot_id: string;
  chantier_id: string;
  statut: BonSortieStatut;
  notes: string | null;
  date_sortie: string | null;
  date_validation: string | null;
  lignes: LigneBonSortieDTO[];
  valeur_totale: number;
}

export interface BonSortieInput {
  depot_id: string;
  chantier_id: string;
  date_sortie?: string | null;
  notes?: string | null;
  lignes: { article_id: string; quantite: number }[];
}

export interface TopSortieChantierDTO { chantier: string; valeur: number }
export interface DashboardStockDTO {
  valeur_totale: number;
  nb_articles: number;
  nb_sous_seuil: number;
  nb_depots: number;
  top_sorties: TopSortieChantierDTO[];
}

export interface TransfertInput {
  article_id: string;
  depot_source_id: string;
  depot_cible_id: string;
  quantite: number;
  motif?: string | null;
  date_transfert?: string | null;
}
export interface TransfertDTO {
  reference: string | null;
  article_id: string;
  article_designation: string;
  depot_source: string;
  depot_cible: string;
  quantite: number;
  valeur: number;
  motif: string | null;
  date_mouvement: string | null;
}

export type InventaireStatut = 'BROUILLON' | 'VALIDE' | 'ANNULE';
export interface LigneInventaireDTO {
  id: string;
  article_id: string;
  quantite_theorique: number;
  quantite_physique: number;
  ecart: number;
  valeur_ecart: number;
}
export interface InventaireDTO {
  id: string;
  reference: string | null;
  depot_id: string;
  statut: InventaireStatut;
  notes: string | null;
  date_inventaire: string | null;
  date_validation: string | null;
  lignes: LigneInventaireDTO[];
  valeur_ecart_total: number;
  nb_ecarts: number;
}

export interface ReapproLigneDTO {
  article_id: string;
  reference: string | null;
  designation: string;
  unite: string | null;
  stock_total: number;
  seuil_min: number;
  quantite_suggeree: number;
  cmp_courant: number;
}

/* --------------------------------- Articles ------------------------------- */
export async function listArticles(params?: { q?: string; size?: number }): Promise<Page<ArticleDTO>> {
  const { data } = await api.get<Page<ArticleDTO>>('/articles', { params });
  return data;
}
export async function createArticle(payload: ArticleInput): Promise<ArticleDTO> {
  const { data } = await api.post<ArticleDTO>('/articles', payload);
  return data;
}
export async function updateArticle(id: string, payload: Partial<ArticleInput>): Promise<ArticleDTO> {
  const { data } = await api.put<ArticleDTO>(`/articles/${id}`, payload);
  return data;
}
export async function getArticleStock(id: string): Promise<StockDepotDTO[]> {
  const { data } = await api.get<StockDepotDTO[]>(`/articles/${id}/stock`);
  return data;
}
export async function getArticleMouvements(id: string): Promise<MouvementDTO[]> {
  const { data } = await api.get<MouvementDTO[]>(`/articles/${id}/mouvements`);
  return data;
}

/* ---------------------------------- Dépôts -------------------------------- */
export async function listDepots(): Promise<DepotDTO[]> {
  const { data } = await api.get<DepotDTO[]>('/depots');
  return data;
}
export async function createDepot(payload: { code?: string | null; libelle: string; type: DepotType; chantier_id?: string | null; adresse?: string | null }): Promise<DepotDTO> {
  const { data } = await api.post<DepotDTO>('/depots', payload);
  return data;
}

/* ------------------------------ Bons de sortie ---------------------------- */
export async function listBonsSortie(): Promise<Page<BonSortieDTO>> {
  const { data } = await api.get<Page<BonSortieDTO>>('/bons-sortie');
  return data;
}
export async function createBonSortie(payload: BonSortieInput): Promise<BonSortieDTO> {
  const { data } = await api.post<BonSortieDTO>('/bons-sortie', payload);
  return data;
}
export async function validerBonSortie(id: string): Promise<BonSortieDTO> {
  const { data } = await api.post<BonSortieDTO>(`/bons-sortie/${id}/valider`);
  return data;
}
export async function deleteBonSortie(id: string): Promise<void> {
  await api.delete(`/bons-sortie/${id}`);
}
export async function openBonSortiePdf(id: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/bons-sortie/${id}/pdf`, undefined, mode);
}

/* ------------------------------ Tableau de bord --------------------------- */
export async function getDashboardStock(): Promise<DashboardStockDTO> {
  const { data } = await api.get<DashboardStockDTO>('/stock/dashboard');
  return data;
}

/* -------------------------------- Transferts ------------------------------ */
export async function listTransferts(): Promise<TransfertDTO[]> {
  const { data } = await api.get<TransfertDTO[]>('/transferts');
  return data;
}
export async function createTransfert(payload: TransfertInput): Promise<TransfertDTO> {
  const { data } = await api.post<TransfertDTO>('/transferts', payload);
  return data;
}

/* -------------------------------- Inventaire ------------------------------ */
export async function listInventaires(): Promise<Page<InventaireDTO>> {
  const { data } = await api.get<Page<InventaireDTO>>('/inventaires');
  return data;
}
export async function getInventaire(id: string): Promise<InventaireDTO> {
  const { data } = await api.get<InventaireDTO>(`/inventaires/${id}`);
  return data;
}
export async function createInventaire(payload: { depot_id: string; notes?: string | null }): Promise<InventaireDTO> {
  const { data } = await api.post<InventaireDTO>('/inventaires', payload);
  return data;
}
export async function saisirInventaire(id: string, lignes: { article_id: string; quantite_physique: number }[]): Promise<InventaireDTO> {
  const { data } = await api.put<InventaireDTO>(`/inventaires/${id}/saisie`, { lignes });
  return data;
}
export async function validerInventaire(id: string): Promise<InventaireDTO> {
  const { data } = await api.post<InventaireDTO>(`/inventaires/${id}/valider`);
  return data;
}
export async function deleteInventaire(id: string): Promise<void> {
  await api.delete(`/inventaires/${id}`);
}

/* --------------------------- Réapprovisionnement -------------------------- */
export async function getReapprovisionnement(): Promise<ReapproLigneDTO[]> {
  const { data } = await api.get<ReapproLigneDTO[]>('/stock/reapprovisionnement');
  return data;
}
